import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, StackProps } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper';
import Pagination from '@/components/Home/@components/Pagination';

type PaginationProps = {
  totalPage: number;
  currPage: number;
  onClickPrev: (...args: any[]) => void;
  onClickNext: (...args: any[]) => void;
  isLoop?: boolean;
  designType?: 'normal' | 'frosted';
  sx?: StackProps['sx'];
};

type BannerCarouselProps<T> = {
  items: T[];
  swiperRef: React.MutableRefObject<SwiperClass | null>;
  onSlideChange?: (swiper: SwiperClass) => void;
  outerRef?: React.Ref<HTMLDivElement>;
  containerSx?: StackProps['sx'];
  style?: React.CSSProperties;
  slideStyle?: React.CSSProperties;
  loop?: boolean;
  pagination?: PaginationProps;
  overlay?: React.ReactNode;
  renderSlide: (item: T, index: number) => React.ReactNode;
  bleedGuardPx?: number; // 양쪽 가장자리 잘라낼 픽셀
  resizeDebounceMs?: number; // 리사이즈 디바운스(ms) 기본 120
  resizeMinDeltaPx?: number; // 최소 변화 픽셀(폭/높이) 기본 1
  useSwiperObservers?: boolean; // 기본 false
};

function BannerCarousel<T>({
  items,
  swiperRef,
  onSlideChange,
  outerRef,
  containerSx,
  style,
  slideStyle,
  loop,
  pagination,
  overlay,
  renderSlide,
  bleedGuardPx = 0.5,
  resizeDebounceMs = 100,
  resizeMinDeltaPx = 1,
  useSwiperObservers = false,
}: BannerCarouselProps<T>) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  const isLoop = typeof loop === 'boolean' ? loop : items.length > 1;
  const [resizing, setResizing] = useState(false);

  const lastSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });
  const tRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      innerRef.current = el;
      if (!outerRef) return;
      if (typeof outerRef === 'function') {
        outerRef(el);
      } else {
        (outerRef as React.MutableRefObject<HTMLDivElement | null>).current =
          el;
      }
    },
    [outerRef],
  );

  // 스냅 + update를 디바운싱하여 스케줄
  const scheduleSnapUpdate = (s: SwiperClass) => {
    setResizing(true);
    if (tRef.current) {
      window.clearTimeout(tRef.current);
      tRef.current = null;
    }
    tRef.current = window.setTimeout(() => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      rafRef.current = requestAnimationFrame(() => {
        const real = (s as any).realIndex ?? (s as any).activeIndex ?? 0;
        (s as any).slideToLoop
          ? (s as any).slideToLoop(real, 0, false)
          : s.slideTo(real, 0, false);
        s.update?.();
        setResizing(false);
      });
    }, resizeDebounceMs);
  };

  useEffect(() => {
    // 언마운트 시 타이머/RAF 정리
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <Stack
      ref={setContainerRef}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        // clipPath는 필요시에만: bleedGuardPx > 0 인 경우에만 적용
        ...(bleedGuardPx > 0
          ? { clipPath: `inset(0 ${bleedGuardPx}px 0 ${bleedGuardPx}px)` }
          : {}),
        ...containerSx,
      }}
    >
      <Swiper
        spaceBetween={resizing ? 300 : 0}
        slidesPerView={1}
        loop={isLoop}
        draggable
        simulateTouch
        grabCursor
        observer={useSwiperObservers} // 기본 false
        observeParents={useSwiperObservers} // 기본 false
        resizeObserver={false}
        updateOnWindowResize={false}
        roundLengths
        loopAdditionalSlides={Math.min(2, items.length)}
        onResize={(s) => {
          // 최소 변화폭(정수 px 기준) 미만이면 스킵
          const el = innerRef.current;
          const w = Math.round(el?.clientWidth ?? (s as any).width ?? 0);
          const h = Math.round(el?.clientHeight ?? (s as any).height ?? 0);
          const { w: lw, h: lh } = lastSizeRef.current;
          if (
            Math.abs(w - lw) < resizeMinDeltaPx &&
            Math.abs(h - lh) < resizeMinDeltaPx
          ) {
            return;
          }
          lastSizeRef.current = { w, h };
          scheduleSnapUpdate(s);
        }}
        onSetTranslate={(s: SwiperClass, translate: number) => {
          // 트랜스폼 정수 보정 (서브픽셀 블리드 방지)
          const t = Math.round(translate);
          s.wrapperEl.style.transform = `translate3d(${t}px, 0, 0)`;
        }}
        onSwiper={(swiper) => (swiperRef.current = swiper)}
        onSlideChange={onSlideChange}
        style={style ?? { width: '100%', height: '100%' }}
      >
        {items.map((item, index) => (
          <SwiperSlide
            key={(item as any)?.id ?? index}
            style={{
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              userSelect: 'none',
              ...slideStyle,
            }}
          >
            {renderSlide(item, index)}
          </SwiperSlide>
        ))}
      </Swiper>

      {overlay}

      {pagination && (
        <Box
          sx={{
            zIndex: 1,
            position: 'absolute',
            right: '8px',
            bottom: '8px',
            ...(pagination.sx || {}),
          }}
        >
          <Pagination
            designType={pagination.designType ?? 'frosted'}
            totalPage={pagination.totalPage}
            currentPage={pagination.currPage}
            onClickPrev={pagination.onClickPrev}
            onClickNext={pagination.onClickNext}
            isLoop={pagination.isLoop ?? isLoop}
          />
        </Box>
      )}
    </Stack>
  );
}

export default BannerCarousel;
